/**
 * Revenue Section
 *
 * MRR card + revenue area chart + plan donut chart.
 */

import React from 'react';
import { motion } from 'framer-motion';
import {
  AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { DollarSign, TrendingDown, Users, CreditCard } from 'lucide-react';
import type { RevenueMetrics } from '../../../types/analytics';
import { StatCardSkeleton, ChartSkeleton } from './AnalyticsSkeleton';

const PLAN_COLORS: Record<string, string> = {
  free: '#94a3b8',
  starter: '#3b82f6',
  pro: '#a855f7',
  professional: '#a855f7',
  agency: '#f59e0b',
};

interface RevenueSectionProps {
  revenue: RevenueMetrics | null;
  loading: boolean;
}

export const RevenueSection: React.FC<RevenueSectionProps> = ({ revenue, loading }) => {
  if (loading || !revenue) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <StatCardSkeleton />
          <StatCardSkeleton />
          <StatCardSkeleton />
          <StatCardSkeleton />
        </div>
        <ChartSkeleton />
      </div>
    );
  }

  const formatCurrency = (n: number) =>
    n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${n.toFixed(0)}`;

  return (
    <div className="space-y-4">
      {/* Revenue KPI Cards */}
      <div className="grid grid-cols-2 gap-3">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 rounded-lg bg-emerald-100">
              <DollarSign className="w-3.5 h-3.5 text-emerald-600" />
            </div>
            <p className="text-[10px] font-semibold uppercase text-gray-400">MRR</p>
          </div>
          <p className="text-2xl font-extrabold text-gray-900">{formatCurrency(revenue.mrr)}</p>
          <p className="text-[10px] text-gray-400">ARR: {formatCurrency(revenue.arr)}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 rounded-lg bg-red-100">
              <TrendingDown className="w-3.5 h-3.5 text-red-600" />
            </div>
            <p className="text-[10px] font-semibold uppercase text-gray-400">Churn</p>
          </div>
          <p className={`text-2xl font-extrabold ${revenue.churn_rate > 5 ? 'text-red-600' : 'text-gray-900'}`}>
            {revenue.churn_rate}%
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 rounded-lg bg-purple-100">
              <CreditCard className="w-3.5 h-3.5 text-purple-600" />
            </div>
            <p className="text-[10px] font-semibold uppercase text-gray-400">LTV</p>
          </div>
          <p className="text-2xl font-extrabold text-gray-900">{formatCurrency(revenue.ltv)}</p>
          <p className="text-[10px] text-gray-400">ARPU: {formatCurrency(revenue.arpu)}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 rounded-lg bg-blue-100">
              <Users className="w-3.5 h-3.5 text-blue-600" />
            </div>
            <p className="text-[10px] font-semibold uppercase text-gray-400">Paying</p>
          </div>
          <p className="text-2xl font-extrabold text-gray-900">{revenue.paying_users}</p>
        </motion.div>
      </div>

      {/* Revenue Trend Area Chart */}
      {revenue.revenue_trend.length > 0 && (
        <div className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Revenue Trend (30d)</h3>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={revenue.revenue_trend}>
              <defs>
                <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#a855f7" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10 }}
                tickFormatter={(d) => new Date(d).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
              />
              <YAxis
                tick={{ fontSize: 10 }}
                width={40}
                tickFormatter={(v) => `$${v}`}
              />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 12, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                formatter={(value: unknown) => [`$${Number(value ?? 0).toFixed(2)}`, 'Revenue']}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#a855f7"
                strokeWidth={2}
                fill="url(#revenueGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Plan Distribution Donut */}
      {revenue.plan_distribution.length > 0 && (
        <div className="bg-white/[0.92] backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Plan Distribution</h3>
          <div className="flex items-center gap-4">
            <ResponsiveContainer width={140} height={140}>
              <PieChart>
                <Pie
                  data={revenue.plan_distribution}
                  dataKey="count"
                  nameKey="plan"
                  cx="50%"
                  cy="50%"
                  innerRadius={35}
                  outerRadius={60}
                  paddingAngle={2}
                >
                  {revenue.plan_distribution.map((entry) => (
                    <Cell
                      key={entry.plan}
                      fill={PLAN_COLORS[entry.plan] || '#94a3b8'}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 12, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2">
              {revenue.plan_distribution.map((entry) => (
                <div key={entry.plan} className="flex items-center gap-2">
                  <div
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: PLAN_COLORS[entry.plan] || '#94a3b8' }}
                  />
                  <span className="text-xs capitalize text-gray-600">{entry.plan}</span>
                  <span className="text-xs font-bold text-gray-900">{entry.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
